/**
 * Tracked Committees Hook
 *
 * Hook for managing tracked committees using the shared tracking factory.
 */

import { createTrackingHook, createTrackedIdsHook } from './useTracking';
import type { TrackedCommittee } from '@/types/v2';

// Configuration for committee tracking
const COMMITTEE_TRACKING_CONFIG = {
  tableName: 'tracked_committees',
  queryKey: 'tracked-committees',
  entityIdField: 'committee_id',
  tierLimitKey: 'trackedCommittees' as const,
  selectColumns: 'id, user_id, committee_id, tracked_at',
  getEntityId: (item: TrackedCommittee) => item.committee_id,
  entityName: 'committee',
};

// Create the base tracking hook
const useBaseTrackedCommittees = createTrackingHook<TrackedCommittee>(COMMITTEE_TRACKING_CONFIG);

/**
 * Hook to manage tracked committees for the current user
 *
 * @returns Object with tracked committees data and mutation functions
 *
 * @example
 * const {
 *   trackedCommittees,
 *   trackedCommitteeIds,
 *   isTracked,
 *   toggleTracking,
 *   isLoading
 * } = useTrackedCommittees();
 *
 * // Check if a committee is tracked
 * if (isTracked(123)) { ... }
 *
 * // Toggle tracking
 * await toggleTracking(123);
 */
export function useTrackedCommittees() {
  const base = useBaseTrackedCommittees();

  // Return with committee-specific naming for backwards compatibility
  return {
    // Data
    trackedCommittees: base.trackedItems,
    trackedCommitteeIds: base.trackedIds,
    trackedCount: base.trackedCount,

    // Tier limits
    committeeLimit: base.limit,
    canTrackMore: base.canTrackMore,
    remainingSlots: base.remainingSlots,

    // State
    isLoading: base.isLoading,
    isError: base.isError,
    error: base.error,

    // Helpers
    isTracked: base.isTracked,

    // Mutations (expose with committee-specific names)
    trackCommittee: base.trackMutation,
    untrackCommittee: base.untrackMutation,

    // Convenience
    toggleTracking: base.toggleTracking,
    isToggling: base.isToggling,

    // Refetch
    refetch: base.refetch,
  };
}

// Create base tracked IDs hook
const useBaseTrackedCommitteeIds =
  createTrackedIdsHook<TrackedCommittee>(COMMITTEE_TRACKING_CONFIG);

/**
 * Hook to get just the tracked committee IDs (lightweight)
 * Useful for components that only need to check if a committee is tracked
 */
export function useTrackedCommitteeIds() {
  const base = useBaseTrackedCommitteeIds();
  return {
    trackedCommitteeIds: base.trackedIds,
    isLoading: base.isLoading,
    isTracked: base.isTracked,
  };
}
